<template>
  <variable-content :id="$attrs['id']">
    <template slot="desktop">
      <spinner-overlay v-if="!isEmpty(value) && guestLoaded === false" />
      <guest-search v-if="search"
                    ref="guestSearch"
                    v-model="internalValue"
                    :disabled="disabled"
                    :label="isEmpty(label) ? $t('inputgroups.compactGuestProfileEditor.label') : label"
                    :type="type" />
      <hm-col v-if="profile !== null" :grid="true" cols="12">
        <hm-input-select v-model="profile.type"
                         :disabled="inputsDisabled"
                         :label="$t('inputgroups.compactGuestProfileEditor.type')"
                         name="type"
                         enum-name="ProfileType" />
        <hm-input v-if="profile.type !== 'Guest'"
                  v-model="profile.companyName"
                  :disabled="!profile.unlocked || inputsDisabled"
                  :label="$t('inputgroups.compactGuestProfileEditor.companyName')"
                  :icons-right="profile.unlocked ? 0 : 1"
                  name="companyName"
                  @blur="updateSearch()">
          <template slot="icon-inline-right" slot-scope="slot">
            <input-icon-button v-tooltip="{ title: $t('inputgroups.guestProfileEditor.unlockGuestName'), delay: 0, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }"
                               v-if="!profile.unlocked"
                               :border="true"
                               :disabled="inputsDisabled"
                               icon="unlock"
                               @click="profile.unlocked = true" />
          </template>
        </hm-input>
        <hm-input v-model="profile.namePrefix"
                  :disabled="inputsDisabled"
                  :label="$t('inputgroups.compactGuestProfileEditor.namePrefix')"
                  name="namePrefix"
                  cols="12 2@tablet"
                  hidden="mobile"
                  @blur="updateSearch()" />
        <hm-input v-model="profile.firstName"
                  :disabled="!profile.unlocked || inputsDisabled"
                  :label="$t('inputgroups.compactGuestProfileEditor.firstName')"
                  :icons-right="profile.unlocked ? 0 : 1"
                  name="firstName"
                  cols="12 5@tablet"
                  @blur="updateSearch()">
          <template slot="icon-inline-right" slot-scope="slot">
            <input-icon-button v-tooltip="{ title: $t('inputgroups.guestProfileEditor.unlockGuestName'), delay: 0, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }"
                               v-if="!profile.unlocked"
                               :border="true"
                               :disabled="inputsDisabled"
                               icon="unlock"
                               @click="profile.unlocked = true" />
          </template>
        </hm-input>
        <hm-input v-model="profile.lastName"
                  :disabled="!profile.unlocked || inputsDisabled"
                  :label="$t('inputgroups.compactGuestProfileEditor.lastName')"
                  :icons-right="profile.unlocked ? 0 : 1"
                  name="lastName"
                  cols="12 5@tablet"
                  @blur="updateSearch()" >
          <template slot="icon-inline-right" slot-scope="slot">
            <input-icon-button v-tooltip="{ title: $t('inputgroups.guestProfileEditor.unlockGuestName'), delay: 0, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }"
                               v-if="!profile.unlocked"
                               :border="true"
                               :disabled="inputsDisabled"
                               icon="unlock"
                               @click="profile.unlocked = true" />
          </template>
        </hm-input>
        <hm-textarea :lazy="true"
                     v-model="profile.primaryAddress.address"
                     :input-rows="2"
                     :disabled="inputsDisabled"
                     :label="$t('inputgroups.compactGuestProfileEditor.address')"
                     name="address" />
        <hm-input-zip-city :zip="profile.primaryAddress.zipCode"
                           :city="profile.primaryAddress.city"
                           :country="profile.primaryAddress.country"
                           :disabled="inputsDisabled"
                           @update:zip="profile.primaryAddress.zipCode = $event"
                           @update:city="profile.primaryAddress.city = $event"
                           @update:country="profile.primaryAddress.country = $event" />
        <hm-select :lazy="true"
                   v-model="profile.primaryAddress.country"
                   :options="getI18nCountries($shortLocale)"
                   :disabled="inputsDisabled"
                   :label="$t('inputgroups.compactGuestProfileEditor.country')"
                   name="country" />
        <hm-input id="primaryEMailAddress"
                  :validate="'email'"
                  :lazy="true"
                  :disabled="inputsDisabled"
                  v-model="profile.primaryEMailAddress"
                  :label="$t('inputgroups.guestProfileEditor.primaryEMailAddress')"
                  name="primaryEMailAddress" />
        <slot :profile="profile" :disabled="inputsDisabled" />
      </hm-col>
    </template>
  </variable-content>
</template>

<script>

import GridMixin from '@/mixins/GridMixin';
import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { isNil, isEmpty, isEqual, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmTextarea from '../inputs/HmTextarea';
import GuestSearch from '../inputs/GuestSearch';

export default {
  name: 'CompactProfileEditor',
  components: { GuestSearch, HmTextarea },
  mixins: [GridMixin, HotelmeisterMixin, SaveWatchMixin],

  props: {
    id: {
      required: false,
      default: () => {
        const randomString = function (n) {
          return Array(n)
            .fill(null)
            .map(() => (Math.random() < 0.5 ? ((Math.random() * 100) % 25) + 'A'.charCodeAt(0) : ((Math.random() * 100) % 25) + 'a'.charCodeAt(0)))
            .map(a => String.fromCharCode(a))
            .join('');
        };
        return `comp_${randomString(5)}`;
      },
    },
    value: {
      required: true,
    },
    label: {
      required: false,
      default: null,
      type: String,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
    search: {
      required: false,
      type: Boolean,
      default: true,
    },
  },

  autosave: [
    {
      name: 'profile',
      variable: 'profile',
      fn: function (cbSaveComplete) {
        if (!isEmpty(this.internalValue)) {
          this.axios.put(this.serviceUrls.guest.get(this, this.internalValue), this.profile)
            .then((response) => {
              cbSaveComplete();
              this.showMessages(response);
            })
            .catch((error) => {
              this.showMessages(error.response);
              cbSaveComplete();
            })
            .finally(() => {
              this.guestLoaded = true;
              this.updateSearch();
              this.$emit('saved');
            });
        } else if (!this.isEmptyProfile(this.profile)) {
          this.axios.post(this.serviceUrls.guest.get(this, ''), this.profile)
            .then((response) => {
              this.profile.id = response.data.data;
              this.internalValue = response.data.data;
              this.profile.unlocked = true;
              cbSaveComplete();
              this.showMessages(response);
            })
            .catch((error) => {
              this.showMessages(error.response);
              cbSaveComplete();
            })
            .finally(() => {
              this.guestLoaded = true;
              this.updateSearch();
              this.$emit('saved');
            });
        } else {
          cbSaveComplete();
        }
      },
    },
  ],

  data() {
    return {
      internalValue: this.value,
      type: 'Guest',
      profile: {
        type: this.type,
        primaryAddress: {},
        unlocked: true,
      },
      guestLoaded: false,
    };
  },

  computed: {
    inputsDisabled: function () {
      return this.disabled || this.profile.deleted;
    },
  },

  watch: {
    value: function () {
      this.internalValue = this.value;
    },
    internalValue: function (newValue, oldValue) {
      this.$emit('input', this.internalValue);
      if (newValue === this.profile.id) return;

      if (isNil(newValue)) {
        this.profile = {
          type: this.type,
          primaryAddress: {},
          unlocked: true,
        };
      } else {
        const $vm = this;
        this.axios
          .get(this.serviceUrls.guest.get(this, newValue))
          .then((response) => {
            $vm.profile = {
              unlocked: false,
              ...response.data.data,
            };
          })
          .catch((error) => {
            $vm.profile = {
              type: $vm.type,
              primaryAddress: {},
              unlocked: true,
            };
          })
          .finally(() => { this.guestLoaded = true; });
      }
    },
  },

  created: function () {
    if (isNil(this.internalValue)) {
      this.profile = {
        type: this.type,
        primaryAddress: {},
        unlocked: true,
      };
    } else {
      this.as__pauseAutosave('profile');
      const $vm = this;
      this.axios
        .get(this.serviceUrls.guest.get(this, this.internalValue))
        .then((response) => {
          $vm.profile = {
            unlocked: false,
            ...response.data.data,
          };
        })
        .catch((error) => {
          $vm.profile = {
            type: $vm.type,
            primaryAddress: {},
            unlocked: true,
          };
        })
        .finally(() => {
          this.as__continueAutosave('profile');
          this.guestLoaded = true;
        });
    }
  },

  methods: {
    isEmpty: function (val) {
      return isEmpty(val);
    },
    isEmptyProfile: function (val) {
      if (!isEmpty(val.companyName)) return false;
      if (!isEmpty(val.namePrefix)) return false;
      if (!isEmpty(val.lastName)) return false;
      if (!isEmpty(val.firstName)) return false;
      if (!isEmpty(val.primaryEMailAddress)) return false;
      if (!isEmpty(val.primaryAddress.address)) return false;
      if (!isEmpty(val.primaryAddress.zipCode)) return false;
      if (!isEmpty(val.primaryAddress.city)) return false;
      if (!isEmpty(val.primaryAddress.country)) return false;
      return true;
    },
    updateSearch: function () {
      const guestSearch = this.$refs.guestSearch;
      if (!isNil(guestSearch)) {
        guestSearch.updateValue(null, true);
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/lib/inputgroups/CompactGuestProfileEditor.vue